import {
  programs,
  programsPeopleEnrollmentEvent,
  ProgramsPeopleEnrollmentEventCreatePathParams,
  ProgramsPeopleEnrollmentListResponse,
} from '@fireflyhealth/api'
import { useQueryClient } from '@tanstack/react-query'
import { useMutation, useQuery } from '~/shared/hooks/query'
import { listProgramEnrollmentsKey, useListProgramEnrollments } from '~/entities/program'
async function listPrograms() {
  const { data } = await programs.list()
  return data
}

const listProgramsKey = 'listPrograms'
export const usePrograms = () => {
  const { data, isPending: isLoading } = useQuery(
    [listProgramsKey],
    listPrograms,
    {
      staleTime: Infinity,
    },
    {
      error: 'Failed to fetch programs',
    },
  )

  return { result: data ?? [], isLoading }
}

type ProgramEnrollment = ProgramsPeopleEnrollmentListResponse[number]

export const useListProgramEnrollmentByProgram = (personId: number) => {
  const { data: programEnrollments, isPending: isLoading } = useListProgramEnrollments(personId)
  const programEnrollmentMap = new Map<string, ProgramEnrollment[]>()
  if (programEnrollments) {
    for (const programEnrollment of programEnrollments) {
      const enrollments = programEnrollmentMap.get(programEnrollment.program) ?? []
      enrollments.push(programEnrollment)
      programEnrollmentMap.set(programEnrollment.program, enrollments)
    }
  }
  const result = programEnrollmentMap
  return { result, isLoading }
}

const programEnrollmentEvent = async (
  payload: ProgramsPeopleEnrollmentEventCreatePathParams & {
    // The OpenAPI schema documents the enrollment, not the event body this endpoint reads.
    event: string
    event_date?: string
    reason?: string
  },
) => {
  await programsPeopleEnrollmentEvent.create(
    {
      uid: payload.uid,
      person_id: payload.person_id,
    },
    {
      data: {
        event: payload.event,
        event_date: payload.event_date,
        reason: payload.reason,
      },
    },
  )
  return payload
}

export const useProgramEnrollmentEvent = () => {
  const client = useQueryClient()

  return useMutation(
    programEnrollmentEvent,
    {
      onSuccess: async p => {
        await client.cancelQueries({ queryKey: [listProgramEnrollmentsKey, p.person_id] })
        await client.invalidateQueries({ queryKey: [listProgramEnrollmentsKey, p.person_id] })
      },
    },
    { error: 'Failed to post program enrollment event' },
  )
}
